import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import AnimatedSection from "../components/AnimatedSection";
import LoadingScreen from "../components/LoadingScreen";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const ProjectDetails = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/api/projects/${id}`);
        setProject(res.data);
      } catch (err) {
        console.error("Error fetching project:", err);
        setProject(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProject();
  }, [id]);

  if (loading) {
    return <LoadingScreen text="Loading project..." />;
  }

  if (!project) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Project Not Found
          </h2>
          <Link
            to="/projects"
            className="text-green-600 font-medium hover:underline"
          >
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  const galleryImages = [
    project.coverImage,
    ...(project.images?.map((img) => img.url) || []),
  ].filter(Boolean);

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Hero Section */}
      <div className="relative h-[35vh] sm:h-[50vh] lg:h-[60vh] w-full overflow-hidden">
        <img
          src={project.coverImage || "/placeholder.jpg"}
          alt={project.name || "Project"}
          className="w-full h-full object-cover transform transition-transform duration-500 hover:scale-105"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 max-w-6xl mx-auto px-6 pb-10 text-white">
          <AnimatedSection>
            <p className="text-sm sm:text-base text-green-400 uppercase tracking-wider">
              {project.category || "Project"}
              {project.date && ` · ${project.date}`}
            </p>

            <h1 className="mt-2 text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
              {project.name}
            </h1>
          </AnimatedSection>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Back Button */}
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-green-600 transition mb-8 font-medium"
        >
          <ArrowLeft size={16} />
          Back to Projects
        </Link>

        {/* Description */}
        <AnimatedSection>
          <div className="max-w-3xl text-gray-700 text-lg leading-relaxed mb-12 whitespace-pre-line">
            {project.details || "No description available."}
          </div>
        </AnimatedSection>

        {/* Gallery */}
        {galleryImages.length > 0 && (
          <AnimatedSection delay={0.2}>
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Project Gallery
            </h3>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages
                .filter((img, index, self) => self.indexOf(img) === index)
                .map((img, index) => (
                  <div
                    key={index}
                    className="aspect-[4/3] rounded-lg overflow-hidden shadow-md hover:shadow-lg transition transform hover:scale-105"
                  >
                    <img
                      src={img}
                      alt={`${project.name} ${index + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
            </div>
          </AnimatedSection>
        )}
      </div>
    </div>
  );
};

export default ProjectDetails;
